import ExcelJS from 'exceljs';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useProductCostingState } from '../ProductCostingContext';
import { checkCurrency } from '../../../../Components/helper';
import { getSampleRefLabel, resolveCostingInfoLabel } from '../../../Dashboard/TodoList/CostingTsx/costing-todo.helpers';
import { withoutEmptyRequirements } from '../specification-requirements.helpers';

/** A Sample Ref as the export reads it: the populated sample, labelled by its sample type and number. */
interface SampleRefNamed {
  _id?: string;
  sampleNumber?: string;
  label?: string;
  [key: string]: unknown;
}

/** Anything the export reads by `name`, with the colour-reference fallbacks the grids also use. */
interface NamedRef {
  _id?: string;
  name?: string;
  newReferences?: string;
  colourRef?: string;
  [key: string]: unknown;
}

/** One expanded colourway/size/length line under a costing row. */
interface ExportExpandRow {
  colourway?: NamedRef;
  size?: string;
  length?: NamedRef;
  quantityPerSizCol?: unknown;
  pricePerUnit?: unknown;
  totalPrice?: unknown;
  quantityUnitOfMeasure?: NamedRef;
  isExcluded?: boolean;
  [key: string]: unknown;
}

/** A parent row of the Average Costing / Quantities / Grand Total grids. */
interface ExportParentRow {
  colourway?: NamedRef;
  expandData?: ExportExpandRow[];
  [key: string]: unknown;
}

/** One Bill of Materials block. */
interface ExportBomRow {
  bomType?: NamedRef;
  referenceCode?: string;
  bomDescription?: string;
  isExcluded?: boolean;
  expandData?: ExportExpandRow[];
  [key: string]: unknown;
}

/** A Set Up Cost / Additional Component line. */
/** One Spec Requirements row as the export reads it. */
interface ExportSpecificationRequirementRow {
  requirement?: string;
  specification?: string;
  isFromTemplate?: boolean;
}

interface ExportAdditionalRow {
  colourway?: NamedRef;
  title?: string;
  quantityPerColSizLen?: unknown;
  pricePerUoM?: unknown;
  totalPrice?: unknown;
  quantityUom?: NamedRef;
  isExcluded?: boolean;
  [key: string]: unknown;
}

/** A Testing line. */
interface ExportTestingRow {
  colourway?: NamedRef;
  colourRef?: NamedRef;
  testingCode?: { code?: string; description?: string; type?: NamedRef; bomComponent?: { referenceCode?: string } };
  totalPrice?: unknown;
  pricePerUnit?: unknown;
  isExcluded?: boolean;
  [key: string]: unknown;
}

/** One landed row. The template adds an `<aliasName>` / `<aliasName>CalculatedValue` pair per column. */
interface ExportLandedRow {
  colourWay?: NamedRef;
  colourway?: NamedRef;
  size?: string;
  sizeRange?: unknown[];
  length?: NamedRef;
  lengthRange?: Array<{ name?: string }>;
  landedInfo?: Array<{ tempValue?: unknown; calculatedValue?: unknown; [key: string]: unknown }>;
  [key: string]: unknown;
}

/** A costing-template column, keyed by `aliasName`. */
interface ExportTemplateColumn {
  _id?: string;
  title?: string;
  type?: string;
  aliasName?: string;
  aliasDisplayName?: string;
  isActive?: boolean;
  isDefault?: boolean;
  isEditable?: boolean;
  position?: number;
  [key: string]: unknown;
}

/** The exchange-rate block stamped on a distribution centre's landed information. */
interface ExportExchangeInfo {
  companyCurrency?: string;
  companyCurrencyValue?: string | number;
  supplierCurrency?: string;
  exchangedRate?: string | number;
  manualAdded?: boolean;
  updatedBy?: string;
  updatedAt?: string;
  [key: string]: unknown;
}

/** One distribution centre's landed block as the export walks it. */
interface ExportLandedInformation {
  distributionCentreName: string;
  distributionCentreCurrency: string;
  landedInfoArr: ExportLandedRow[];
  landedExchangeRateInformation: ExportExchangeInfo;
}

/** One extra (non-default) Costing Information field and the value entered against it. */
interface ExportExtraField {
  label?: string | undefined;
  type?: string | undefined;
  value?: string | number | null | undefined;
}

/**
 * The whole costing, flattened into exactly what the spreadsheet needs. Assembled by
 * `createDataFromState` and consumed by nothing else.
 */
interface CostingExportData {
  costingInfo: { defaultLabels: string[]; extraFields: ExportExtraField[] };
  landedInformations: ExportLandedInformation[];
  isAverageCosting: boolean;
  isDetailedCosting: boolean;
  status: string;
  colourWayToggle: boolean;
  sizeLengthToggle: boolean;
  productInfo: {
    supplierId?: NamedRef;
    currency?: string;
    incoterms?: NamedRef;
    reference?: string;
    shipmentMethod?: string;
    targetCost?: unknown;
    factoryId?: { factoryName?: string; [key: string]: unknown };
    moqNeeded?: unknown;
  };
  sampleRef?: SampleRefNamed | undefined;
  productTemplate?:
    | {
        name?: string;
        isAdditionalComponentActive?: boolean;
        isSetUpCostActive?: boolean;
        additionalComponentTitle?: string;
        [key: string]: unknown;
      }
    | undefined;
  averageCosting: {
    averageCostingInfo?: ExportParentRow[];
    sumOfTotalQuantity: unknown;
    totalPricePerUnit: unknown;
    overAllSumOfQuantityPerSizCol: unknown;
    overAllSumOfTotalPrice: unknown;
    overAllSumOfPricePerUnit: unknown;
    totLength?: unknown[];
    totSize?: unknown[];
    totColourways?: unknown[];
  };
  testing: { testingInfo?: ExportTestingRow[]; sumOfPrice: unknown };
  grandTotal: { grandTotalInfo?: ExportParentRow[]; totalBomTypeTotalPrice: unknown; totalOverAllSumTotalPrice: unknown };
  landedInformation: { landedInfoArr: ExportLandedRow[] };
  detailedCosting: { bomTypeArr?: ExportBomRow[] };
  quantities: {
    quantitiesInfo?: ExportParentRow[];
    sumOfTotalQuantity: unknown;
    totalPricePerUnit: unknown;
    sumOfQuantityPerSizCol: unknown;
    overAllSumOfQuantityPerSizCol: unknown;
    totLength?: unknown[];
    totSize?: unknown[];
    totColourways?: unknown[];
  };
  additionalComponent?: { additionalComponentInfo?: ExportAdditionalRow[]; sumOfTotPrice: unknown };
  additional?: { additionalInfo?: ExportAdditionalRow[]; sumOfTotPrice: unknown };
  specificationRequirement?: { specificationRequirementInfo?: ExportSpecificationRequirementRow[] };
}

/** One spreadsheet cell value, as the row builders produce them. */
type ExportCellValue = string | number;

/** The reusable cell styles, built once per workbook. */
interface ExcelStyles {
  sectionTitle: Partial<ExcelJS.Style>;
  tableHeader: Partial<ExcelJS.Style>;
  dataCell: Partial<ExcelJS.Style>;
  numberCell: Partial<ExcelJS.Style>;
  totalRow: Partial<ExcelJS.Style>;
}

type AddSectionTitleFn = (title: string, colSpan: number, row: number) => number;
type AddTableHeadersFn = (headers: string[], row: number) => number;
type AddDataRowFn = (rowData: ExportCellValue[], row: number, isTotal?: boolean, numericColumns?: number[]) => number;

/** Everything the section builders need, resolved once in `generateCostingExcel`. */
interface ExcelSectionConfig {
  showColourwayColumn: boolean;
  showSizeColumn: boolean;
  showLengthColumn: boolean;
  currencySymbol: string;
  data: CostingExportData;
  isDetailedCosting: boolean;
  isAverageCosting: boolean;
}

/** The redux slices this hook reads. */
interface ExcelDownloadRootState {
  newProductCostingData: {
    productLengths?: unknown[];
    productSizes?: string[];
    productColours?: unknown[];
    productExchnageRate?: { defaultCurrencyValue?: string | number };
    productCostingDetail?: { landedInformation?: { landedExchangeRateInformation?: ExportExchangeInfo } } | null;
    costingTemplateDetail?: ExportTemplateDetail;
    isRefreshed?: boolean;
  };
  UserDetails: { userInfo?: { companyCurrency?: string } };
}

/** The costing template, as the export reads it. */
interface ExportTemplateDetail {
  _id?: string;
  isCostingInfoActive?: boolean;
  costingInfo?: ExportTemplateColumn[];
  topColumnArr?: ExportTemplateColumn[];
  defaultColumns?: ExportTemplateColumn[];
  bottomColumnArr?: ExportTemplateColumn[];
  landedInfo?: ExportTemplateColumn[];
  [key: string]: unknown;
}

/**
 * `parseFloat(x || 0)` throughout the original relies on JavaScript coercing a number to a string
 * before parsing. TypeScript requires the string, so the coercion is made explicit here — the
 * `|| 0` is kept inside so a blank string still resolves to 0 exactly as before.
 */
const toFloat = (value: unknown): number => parseFloat(String(value || 0));

export interface UseExcelDownloadResult {
  handleCostingDownload: () => Promise<void>;
  generateCostingExcel: (data: CostingExportData) => Promise<void>;
}

/** Builds and downloads the costing spreadsheet from the live context state. */
const useExcelDownload = (): UseExcelDownloadResult => {
  const {
    colSizeLengthToggleState: { colourWayToggle, sizeLengthToggle },
    productCostingstate,
    internalState,
    landedInfoState,
    landedInfoStates,
    additionalState,
    testingState,
    grandTotalState,
    detailedCostingState,
    detailedQuanitiesState,
    additionalComponentState,
    specificationRequirementState,
  } = useProductCostingState();

  const {
    newProductCostingData: { productLengths, productSizes, productColours, productExchnageRate, productCostingDetail, costingTemplateDetail, isRefreshed },
    UserDetails: { userInfo },
  } = useSelector((reducer: ExcelDownloadRootState) => reducer);

  const { productid } = useParams<{ productid?: string }>();
  const {
    averageCosting: { averageCostingActive },
    productInfo,
    productTemplate: productTemplateValue,
    sampleRef,
    averageCosting,
    status,
    isBreakdown,
    landedInformationColumns,
    costingSizes,
  } = productCostingstate;
  const productTemplate = productTemplateValue as ExportTemplateDetail | null;

  /**
   * Sizes saved on the costing itself. The credential-less supplier link never
   * fetches the dev-order size master, so `productSizes` is empty there; the
   * costing document is the only source that works for both entry points.
   */
  const effectiveSizes = costingSizes?.length ? costingSizes : productSizes;

  const {
    detailedCosting: { detailedCostingActive, bomTypeArr },
  } = detailedCostingState;

  const createDataFromState = async (): Promise<CostingExportData> => {
    const mergedLandedInfoArr =
      landedInfoStates.length > 0 ? landedInfoStates.flatMap((s) => s.landedInfo?.landedInfoArr || []) : landedInfoState?.landedInfo?.landedInfoArr || [];

    const landedInformations: ExportLandedInformation[] =
      landedInfoStates.length > 0
        ? landedInfoStates.map((s) => ({
            distributionCentreName: s?.distributionCentre?.distributionName || '',
            distributionCentreCurrency: s?.distributionCentre?.currency || '',
            landedInfoArr: (s?.landedInfo?.landedInfoArr || []) as ExportLandedRow[],
            landedExchangeRateInformation: (s?.landedExchangeRateInformation || {}) as ExportExchangeInfo,
          }))
        : [
            {
              distributionCentreName: '',
              distributionCentreCurrency: '',
              landedInfoArr: (landedInfoState?.landedInfo?.landedInfoArr || []) as ExportLandedRow[],
              landedExchangeRateInformation: (productCostingDetail?.landedInformation?.landedExchangeRateInformation || {}) as ExportExchangeInfo,
            },
          ];

    const liveTemplateDetail =
      costingTemplateDetail && productTemplate && costingTemplateDetail._id === productTemplate._id ? costingTemplateDetail : productTemplate;
    const useLiveTemplate = !productid || isRefreshed;
    const isCostingInfoActive = liveTemplateDetail?.isCostingInfoActive ?? productTemplate?.isCostingInfoActive ?? false;
    const defaultCostingInfo = (liveTemplateDetail?.costingInfo ?? []).filter((item) => item.isDefault);
    /** `slot` is a canonical 0-based index into the default field order, not the stored `position` — see `resolveCostingInfoLabel`. */
    const getCostingInfoLabel = (slot: number, fallback: string): string =>
      resolveCostingInfoLabel(defaultCostingInfo, slot, fallback, isCostingInfoActive);
    const extraCostingInfo = isCostingInfoActive
      ? ((useLiveTemplate ? liveTemplateDetail?.costingInfo : productTemplate?.costingInfo) ?? []).filter((item) => !item.isDefault)
      : [];

    const responsePayload: CostingExportData = {
      costingInfo: {
        defaultLabels: [
          getCostingInfoLabel(0, 'Reference'),
          getCostingInfoLabel(1, 'Supplier'),
          getCostingInfoLabel(2, 'Factory'),
          getCostingInfoLabel(3, 'Costing Template'),
          getCostingInfoLabel(4, 'Currency'),
          getCostingInfoLabel(5, 'MOQ Needed'),
          getCostingInfoLabel(6, 'Target Cost'),
          getCostingInfoLabel(7, 'Incoterms'),
          getCostingInfoLabel(8, 'Shipment Method'),
          getCostingInfoLabel(9, 'Sample Ref'),
        ],
        extraFields: extraCostingInfo.map((item) => ({
          label: item.aliasDisplayName || item.title,
          type: item.type,
          value: (internalState?.costingInfoValues?.[item._id as string] ?? null) as string | number | null,
        })),
      },
      landedInformations,
      isAverageCosting: averageCostingActive,
      isDetailedCosting: detailedCostingActive,
      status,
      colourWayToggle,
      sizeLengthToggle,
      productInfo: {
        supplierId: productInfo?.supplierId as unknown as NamedRef,
        currency: productInfo?.currency as string,
        incoterms: productInfo?.incoterms as unknown as NamedRef,
        reference: productInfo?.reference,
        shipmentMethod: productInfo?.shipmentMethod as string,
        targetCost: productInfo?.targetCost,
        factoryId: productInfo?.factoryId as unknown as { factoryName?: string },
        moqNeeded: productInfo?.moqNeeded,
      },
      sampleRef: sampleRef as SampleRefNamed,
      productTemplate: productTemplate as CostingExportData['productTemplate'],
      averageCosting: {
        averageCostingInfo: averageCosting.averageCostingInfo as ExportParentRow[],
        sumOfTotalQuantity: averageCosting.sumOfTotalQuantity || '0',
        totalPricePerUnit: averageCosting.totalPricePerUnit || '0',
        overAllSumOfQuantityPerSizCol: averageCosting.overAllSumOfQuantityPerSizCol || '0',
        overAllSumOfTotalPrice: averageCosting.overAllSumOfTotalPrice || '0',
        overAllSumOfPricePerUnit: averageCosting.overAllSumOfPricePerUnit || '0',
        totLength: averageCosting?.totLength,
        totSize: averageCosting?.totSize,
        totColourways: averageCosting?.totColourways,
      },
      testing: {
        testingInfo: testingState?.testing?.testingInfo as ExportTestingRow[],
        sumOfPrice: testingState?.testing?.sumOfPrice || '0',
      },
      grandTotal: {
        grandTotalInfo: grandTotalState?.grandTotal as ExportParentRow[],
        totalBomTypeTotalPrice: grandTotalState?.totalBomTypeTotalPrice,
        totalOverAllSumTotalPrice: grandTotalState?.totalOverAllSumTotalPrice,
      },
      landedInformation: { landedInfoArr: mergedLandedInfoArr as ExportLandedRow[] },
      detailedCosting: {
        bomTypeArr: bomTypeArr as unknown as ExportBomRow[],
      },
      quantities: {
        quantitiesInfo: detailedQuanitiesState?.quantities?.quantitiesInfo as ExportParentRow[],
        sumOfTotalQuantity: detailedQuanitiesState?.quantities?.sumOfTotalQuantity || '0',
        totalPricePerUnit: detailedQuanitiesState?.quantities?.totalPricePerUnit || '0',
        sumOfQuantityPerSizCol: detailedQuanitiesState?.quantities?.sumOfQuantityPerSizCol || '0',
        overAllSumOfQuantityPerSizCol: detailedQuanitiesState?.quantities?.overAllSumOfQuantityPerSizCol || '0',
        totLength: detailedQuanitiesState?.quantities?.totLength,
        totSize: detailedQuanitiesState?.quantities?.totSize,
        totColourways: detailedQuanitiesState?.quantities?.totColourways,
      },
    };

    if (productTemplate?.isAdditionalComponentActive) {
      responsePayload.additionalComponent = {
        additionalComponentInfo: additionalComponentState?.additionalComponent?.additionalComponentInfo as ExportAdditionalRow[],
        sumOfTotPrice: additionalComponentState?.additionalComponent?.sumOfTotPrice || '0',
      };
    } else {
      responsePayload.additionalComponent = {
        additionalComponentInfo: [],
        sumOfTotPrice: '0',
      };
    }

    if (productTemplate?.isSetUpCostActive) {
      responsePayload.additional = {
        additionalInfo: additionalState?.additional?.additionalInfo as ExportAdditionalRow[],
        sumOfTotPrice: additionalState?.additional?.sumOfTotPrice || '0',
      };
    } else {
      responsePayload.additional = {
        additionalInfo: [],
        sumOfTotPrice: '0',
      };
    }

    if (productTemplate?.isSpecificationRequirementActive) {
      responsePayload.specificationRequirement = {
        specificationRequirementInfo: withoutEmptyRequirements(
          specificationRequirementState?.specificationRequirement?.specificationRequirementInfo,
        ) as ExportSpecificationRequirementRow[],
      };
    } else {
      responsePayload.specificationRequirement = { specificationRequirementInfo: [] };
    }

    return responsePayload;
  };

  const handleCostingDownload = async (): Promise<void> => {
    try {
      const data = await createDataFromState();
      await generateCostingExcel(data);
    } catch (error) {
      console.error('Error generating Excel file:', error);
    }
  };

  const generateCostingExcel = async (data: CostingExportData): Promise<void> => {
    const userData = JSON.parse(localStorage.getItem('loggedInUserInfo') as string) as { lengthVisible?: boolean | null } | null;
    const isLengthVisible = Boolean(userData?.lengthVisible && userData?.lengthVisible !== null && userData?.lengthVisible !== undefined);

    const isDetailedCosting = data?.isDetailedCosting || false;
    const isAverageCosting = data?.isAverageCosting || false;

    let hideColourCol = false;
    let hideSizeCol = false;
    let hideLengthCol = false;

    if (colourWayToggle) {
      hideColourCol = (productColours?.length ?? 0) > 0;
    }
    if (sizeLengthToggle) {
      hideSizeCol = (effectiveSizes?.length ?? 0) > 0;
      hideLengthCol = (productLengths?.length ?? 0) > 0;
    }

    const showColourwayColumn = colourWayToggle && hideColourCol;
    const showSizeColumn = sizeLengthToggle && hideSizeCol;
    const showLengthColumn = sizeLengthToggle && isLengthVisible && hideLengthCol && (productLengths?.length ?? 0) > 0;

    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'Bombiix PLM';
    workbook.lastModifiedBy = 'System';
    workbook.created = new Date();
    workbook.modified = new Date();

    const worksheet = workbook.addWorksheet('Bombiix Costing Excel Template');
    const productCurrency = data?.productInfo?.currency || productInfo?.currency;

    const currencySymbol = checkCurrency(productCurrency, null, true);
    let currentRow = 1;

    const config: ExcelSectionConfig = {
      showColourwayColumn,
      showSizeColumn,
      showLengthColumn,
      currencySymbol,
      data,
      isDetailedCosting,
      isAverageCosting,
    };

    const { styles, addSectionTitle, addTableHeaders, addDataRow } = createExcelHelpers(worksheet);

    setColumnWidths(worksheet);

    currentRow = addCostingInformation(worksheet, styles, config, currentRow, addSectionTitle, addTableHeaders, addDataRow);
    currentRow = addCoreProductSection(worksheet, styles, config, currentRow, addSectionTitle, addTableHeaders, addDataRow);

    if (isDetailedCosting) {
      currentRow = addBillOfMaterialsSection(worksheet, styles, config, currentRow, addSectionTitle, addTableHeaders, addDataRow);
    }

    currentRow = addAdditionalComponentSection(worksheet, styles, config, currentRow, addSectionTitle, addTableHeaders, addDataRow);
    currentRow = addSetUpCostSection(worksheet, styles, config, currentRow, addSectionTitle, addTableHeaders, addDataRow);
    currentRow = addSpecificationRequirementSection(worksheet, styles, config, currentRow, addSectionTitle, addTableHeaders, addDataRow);
    currentRow = addTestingSection(worksheet, styles, config, currentRow, addSectionTitle, addTableHeaders, addDataRow);
    currentRow = addGrandTotalSection(worksheet, styles, config, currentRow, addSectionTitle, addTableHeaders, addDataRow);
    currentRow = addLandedInfoSection(worksheet, styles, config, currentRow, addSectionTitle, addTableHeaders, addDataRow);

    await saveExcelFile(workbook, data);
  };

  const createExcelHelpers = (
    worksheet: ExcelJS.Worksheet,
  ): {
    styles: ExcelStyles;
    addSectionTitle: AddSectionTitleFn;
    addTableHeaders: AddTableHeadersFn;
    addDataRow: AddDataRowFn;
    formatNumericValue: (value: unknown, includeSymbol?: boolean, currencySymbol?: string) => string;
  } => {
    const colors = {
      sectionHeader: 'FFFFFF',
      tableHeader: 'F3F3F3',
      white: '000000',
      lightBlue: 'F3F3F3',
      lightGray: 'F3F3F3',
    };

    const styles: ExcelStyles = {
      sectionTitle: {
        font: { name: 'Aktiv Grotesk', bold: true, size: 10, color: { argb: '000000' } },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: colors.sectionHeader } },
        alignment: { horizontal: 'left', vertical: 'middle' },
      },
      tableHeader: {
        font: { name: 'Aktiv Grotesk', bold: true, size: 10, color: { argb: '000000' } },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: colors.tableHeader } },
        alignment: { horizontal: 'left', vertical: 'middle' },
        border: {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        },
      },
      dataCell: {
        font: { name: 'Aktiv Grotesk', size: 10 },
        alignment: { horizontal: 'left', vertical: 'middle' },
        border: {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        },
      },
      numberCell: {
        font: { name: 'Aktiv Grotesk', size: 10 },
        alignment: { horizontal: 'left', vertical: 'middle' },
        numFmt: '#,##0.00',
        border: {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        },
      },
      totalRow: {
        font: { name: 'Aktiv Grotesk', bold: true, size: 10 },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF' } },
        alignment: { horizontal: 'left', vertical: 'middle' },
        border: {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        },
      },
    };

    const formatNumericValue = (value: unknown, includeSymbol = false, currencySymbol = ''): string => {
      const numValue = toFloat(value);
      if (includeSymbol && currencySymbol) {
        return `${currencySymbol}${numValue.toFixed(2)}`;
      }
      return numValue.toFixed(2);
    };

    const addSectionTitle: AddSectionTitleFn = (title, colSpan = 10, row) => {
      const cell = worksheet.getCell(row, 1);
      cell.value = title;
      cell.style = styles.sectionTitle;
      worksheet.mergeCells(row, 1, row, colSpan);
      return row + 1;
    };

    const addTableHeaders: AddTableHeadersFn = (headers, row) => {
      headers.forEach((header, index) => {
        const cell = worksheet.getCell(row, index + 1);
        cell.value = header;
        cell.style = styles.tableHeader;
      });
      return row + 1;
    };

    const addDataRow: AddDataRowFn = (rowData, row, isTotal = false, numericColumns = []) => {
      rowData.forEach((value, index) => {
        const cell = worksheet.getCell(row, index + 1);

        if (numericColumns.includes(index) && typeof value === 'number') {
          cell.value = value;
          cell.numFmt = '#,##0.00';
        } else {
          cell.value = value || '';
        }

        if (isTotal) {
          cell.style = styles.totalRow;
        } else if (numericColumns.includes(index)) {
          cell.style = styles.numberCell;
        } else {
          cell.style = styles.dataCell;
        }
      });
      return row + 1;
    };

    return { styles, addSectionTitle, addTableHeaders, addDataRow, formatNumericValue };
  };

  const setColumnWidths = (worksheet: ExcelJS.Worksheet): void => {
    const standardWidth = 23;
    for (let i = 1; i <= 30; i++) {
      worksheet.getColumn(i).width = standardWidth;
    }
  };

  const addCostingInformation = (
    _worksheet: ExcelJS.Worksheet,
    _styles: ExcelStyles,
    config: ExcelSectionConfig,
    currentRow: number,
    addSectionTitle: AddSectionTitleFn,
    addTableHeaders: AddTableHeadersFn,
    addDataRow: AddDataRowFn,
  ): number => {
    const { data } = config;

    const defaultLabels = data.costingInfo?.defaultLabels || [
      'Reference',
      'Supplier',
      'Factory',
      'Costing Template',
      'Currency',
      'MOQ Needed',
      'Target Cost',
      'Incoterms',
      'Shipment Method',
      'Sample Ref',
    ];
    const extraFields = data.costingInfo?.extraFields || [];
    const costingHeaders = [...defaultLabels, ...extraFields.map((field) => field.label as string)];

    currentRow = addSectionTitle('Costing Information', costingHeaders.length, currentRow);
    currentRow = addTableHeaders(costingHeaders, currentRow);

    const formatCostingDate = (value: string | number): string => {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '--';
      const day = String(date.getDate()).padStart(2, '0');
      const month = date.toLocaleString('en-US', { month: 'short' });
      return `${day}-${month}-${date.getFullYear()}`;
    };

    const productInfoData = data.productInfo || {};
    const costingData: ExportCellValue[] = [
      productInfoData.reference || '--',
      productInfoData.supplierId?.name || '--',
      productInfoData.factoryId?.factoryName || '--',
      data.productTemplate?.name || '--',
      productInfoData.currency || '--',
      toFloat(productInfoData.moqNeeded).toFixed(2),
      toFloat(productInfoData.targetCost).toFixed(2),
      productInfoData.incoterms?.name || '--',
      productInfoData.shipmentMethod || '--',
      getSampleRefLabel(data.sampleRef) || '--',
    ];
    extraFields.forEach((field) => {
      if (field.type === 'datePicker' && field.value) {
        costingData.push(formatCostingDate(field.value));
      } else {
        costingData.push(field.value || '--');
      }
    });
    currentRow = addDataRow(costingData, currentRow, false, [5, 6]);

    return currentRow + 1;
  };

  const addCoreProductSection = (
    _worksheet: ExcelJS.Worksheet,
    _styles: ExcelStyles,
    config: ExcelSectionConfig,
    currentRow: number,
    addSectionTitle: AddSectionTitleFn,
    addTableHeaders: AddTableHeadersFn,
    addDataRow: AddDataRowFn,
  ): number => {
    const {
      data,
      showColourwayColumn,
      showSizeColumn,
      showLengthColumn,
      currencySymbol,
      isDetailedCosting,
      isAverageCosting,
    } = config;
    if (isDetailedCosting) {
      if (!data?.quantities?.quantitiesInfo?.length) {
        return currentRow;
      }
    } else if (isAverageCosting) {
      if (!data?.averageCosting?.averageCostingInfo?.length) {
        return currentRow;
      }
    } else {
      return currentRow;
    }

    const buildCoreProductHeaders = (): string[] => {
      const headers: string[] = [];
      if (showColourwayColumn) headers.push('Colourway');
      const shouldShowSizeColumn = showSizeColumn || (!showColourwayColumn && !showSizeColumn);
      if (shouldShowSizeColumn) headers.push('Size');

      if (showLengthColumn) headers.push('Length');
      if (isDetailedCosting) {
        headers.push('Quantity');
      } else {
        headers.push('Quantity', 'Price Per Unit', 'Total Price');
      }
      return headers;
    };

    const buildDataRowFromExpandData = (expandItem: ExportExpandRow, colourwayInfo: ExportParentRow | null): ExportCellValue[] => {
      const row: ExportCellValue[] = [];
      if (showColourwayColumn) {
        if (isDetailedCosting) {
          const colourwayName = expandItem?.colourway?.name || expandItem?.colourway?.newReferences || '';
          row.push(colourwayName);
        } else {
          const colourwayName = colourwayInfo?.colourway?.name || colourwayInfo?.colourway?.newReferences || '';
          row.push(colourwayName);
        }
      }
      const shouldShowSizeColumn = showSizeColumn || (!showColourwayColumn && !showSizeColumn);
      if (shouldShowSizeColumn) {
        if (showSizeColumn) {
          row.push(expandItem?.size?.toUpperCase() || '');
        } else {
          const defaultSizes = effectiveSizes?.length ? effectiveSizes : data?.averageCosting?.totSize || [];
          row.push(defaultSizes.join(', ').toUpperCase());
        }
      }

      if (showLengthColumn) {
        row.push(expandItem.length?.name || '');
      }

      const quantity = toFloat(expandItem?.quantityPerSizCol);

      if (isDetailedCosting) {
        row.push(quantity.toFixed(2));
      } else {
        const pricePerUnit = toFloat(expandItem?.pricePerUnit);
        const totalPrice = toFloat(expandItem?.totalPrice);
        row.push(quantity.toFixed(2), pricePerUnit.toFixed(2), `${currencySymbol}${totalPrice.toFixed(2)}`);
      }
      return row;
    };

    const getNumericColumnIndices = (): number[] => {
      const indices: number[] = [];
      let colIndex = 0;
      if (showColourwayColumn) colIndex++;

      const shouldShowSizeColumn = showSizeColumn || (!showColourwayColumn && !showSizeColumn);
      if (shouldShowSizeColumn) colIndex++;

      if (showLengthColumn) colIndex++;
      indices.push(colIndex);

      if (!isDetailedCosting) {
        indices.push(colIndex + 1);
      }
      return indices;
    };

    const dynamicHeaders = buildCoreProductHeaders();
    const numericIndices = getNumericColumnIndices();
    const maxCols = dynamicHeaders.length;

    currentRow = addSectionTitle('Core Product', maxCols, currentRow);
    currentRow = addTableHeaders(dynamicHeaders, currentRow);

    let coreProductTotal = 0;
    let totalQuantity = 0;

    if (isDetailedCosting) {
      data?.quantities?.quantitiesInfo?.forEach((quantityInfo) => {
        if (quantityInfo?.expandData && Array.isArray(quantityInfo.expandData)) {
          quantityInfo.expandData.forEach((expandItem) => {
            const dataRow = buildDataRowFromExpandData(expandItem, null);
            currentRow = addDataRow(dataRow, currentRow, false, numericIndices);

            const quantity = toFloat(expandItem?.quantityPerSizCol);
            totalQuantity += quantity;
          });
        }
      });
    } else {
      data?.averageCosting?.averageCostingInfo?.forEach((colourwayInfo) => {
        if (colourwayInfo?.expandData && Array.isArray(colourwayInfo.expandData)) {
          colourwayInfo.expandData.forEach((expandItem) => {
            const dataRow = buildDataRowFromExpandData(expandItem, colourwayInfo);
            currentRow = addDataRow(dataRow, currentRow, false, numericIndices);

            const quantity = toFloat(expandItem?.quantityPerSizCol);
            const totalPrice = toFloat(expandItem?.totalPrice);
            totalQuantity += quantity;
            coreProductTotal += totalPrice;
          });
        }
      });
    }

    const totalRow: ExportCellValue[] = [];

    if (isDetailedCosting) {
      if (showColourwayColumn) {
        const uniqueColourways = data.quantities?.totColourways || [];
        totalRow.push(`${uniqueColourways.length} Colourway(s)`);
      }

      const shouldShowSizeColumn = showSizeColumn || (!showColourwayColumn && !showSizeColumn);
      if (shouldShowSizeColumn) {
        const uniqueSizes = data.quantities?.totSize || [];
        totalRow.push(`${uniqueSizes.length} Size(s)`);
      }

      if (showLengthColumn) {
        const uniqueLengths = data.quantities?.totLength || [];
        totalRow.push(`${uniqueLengths.length} Length(s)`);
      }

      totalRow.push(`${totalQuantity} Units`);
    } else {
      if (showColourwayColumn) {
        totalRow.push(`${data?.averageCosting?.averageCostingInfo?.length || 0} Colourway(s)`);
      }

      const shouldShowSizeColumn = showSizeColumn || (!showColourwayColumn && !showSizeColumn);
      if (shouldShowSizeColumn) {
        const uniqueSizes = effectiveSizes?.length ? effectiveSizes : data?.averageCosting?.totSize || [];
        totalRow.push(`${uniqueSizes.length} Size(s)`);
      }

      if (showLengthColumn) {
        const uniqueLengths = data.averageCosting?.totLength || [];
        totalRow.push(`${uniqueLengths.length} Length(s)`);
      }

      totalRow.push(`${totalQuantity} Units`, '', `${currencySymbol}${coreProductTotal.toFixed(2)}`);
    }

    currentRow = addDataRow(totalRow, currentRow, true, numericIndices);

    return currentRow + 1;
  };

  const addBillOfMaterialsSection = (
    _worksheet: ExcelJS.Worksheet,
    _styles: ExcelStyles,
    config: ExcelSectionConfig,
    currentRow: number,
    addSectionTitle: AddSectionTitleFn,
    addTableHeaders: AddTableHeadersFn,
    addDataRow: AddDataRowFn,
  ): number => {
    const { data, showColourwayColumn, showSizeColumn, showLengthColumn, currencySymbol } = config;

    if (!data?.detailedCosting?.bomTypeArr?.length) {
      return currentRow;
    }

    const buildBOMHeaders = (): string[] => {
      const headers: string[] = [];
      if (showColourwayColumn) headers.push('Colourway');
      if (showSizeColumn) headers.push('Size');
      if (showLengthColumn) headers.push('Length');
      headers.push('BOM Type', 'Component Code', 'Component Description', 'Placement', 'Quantity', 'UoM', 'Price Per Unit', 'Total Price', 'Exclude');
      return headers;
    };

    const buildBOMDataRowFromExpandData = (expandItem: ExportExpandRow, bomItem: ExportBomRow): ExportCellValue[] => {
      const row: ExportCellValue[] = [];

      if (showColourwayColumn) {
        row.push(expandItem?.colourway?.name || expandItem?.colourway?.newReferences || '');
      }
      if (showSizeColumn) {
        row.push((expandItem?.size || '').toUpperCase());
      }
      if (showLengthColumn) {
        row.push(expandItem?.length?.name || '');
      }

      const quantity = toFloat(expandItem?.quantityPerSizCol);
      const pricePerUnit = toFloat(expandItem?.pricePerUnit);
      const totalPrice = toFloat(expandItem?.totalPrice);
      const isExcluded = expandItem?.isExcluded || bomItem?.isExcluded || false;
      const excludeSymbol = isExcluded ? '✓' : '✗';

      row.push(
        bomItem?.bomType?.name || '',
        bomItem?.referenceCode || '',
        bomItem?.bomDescription || '',
        '',
        quantity.toFixed(2),
        expandItem?.quantityUnitOfMeasure?.name || '',
        `${currencySymbol}${pricePerUnit.toFixed(2)}`,
        `${currencySymbol}${totalPrice.toFixed(2)}`,
        excludeSymbol,
      );

      return row;
    };

    const getBOMNumericIndices = (): number[] => {
      const indices: number[] = [];
      let colIndex = 0;

      if (showColourwayColumn) colIndex++;
      if (showSizeColumn) colIndex++;
      if (showLengthColumn) colIndex++;

      colIndex += 3;
      colIndex++;
      indices.push(colIndex);
      colIndex++;
      indices.push(colIndex);
      indices.push(colIndex + 1);

      return indices;
    };

    const bomHeaders = buildBOMHeaders();
    const bomNumericIndices = getBOMNumericIndices();
    const maxBOMCols = bomHeaders.length;

    currentRow = addSectionTitle('Bill of Materials', maxBOMCols, currentRow);
    currentRow = addTableHeaders(bomHeaders, currentRow);

    let bomTotal = 0;

    data?.detailedCosting?.bomTypeArr?.forEach((bomItem) => {
      if (bomItem?.expandData && Array.isArray(bomItem.expandData)) {
        bomItem.expandData.forEach((expandItem) => {
          const dataRow = buildBOMDataRowFromExpandData(expandItem, bomItem);
          currentRow = addDataRow(dataRow, currentRow, false, bomNumericIndices);

          const isExcluded = expandItem?.isExcluded || bomItem?.isExcluded || false;
          if (!isExcluded) {
            const totalPrice = toFloat(expandItem?.totalPrice);
            bomTotal += totalPrice;
          }
        });
      }
    });

    const buildBOMTotalRow = (total: number): ExportCellValue[] => {
      const row: ExportCellValue[] = [];
      if (showColourwayColumn) row.push('');
      if (showSizeColumn) row.push('');
      if (showLengthColumn) row.push('');
      row.push('', '', '', '', '', '', '', `${currencySymbol}${total.toFixed(2)}`, '');
      return row;
    };

    const totalRow = buildBOMTotalRow(bomTotal);
    currentRow = addDataRow(totalRow, currentRow, true, [bomNumericIndices[bomNumericIndices.length - 2] as number]);

    return currentRow + 1;
  };

  const addAdditionalComponentSection = (
    _worksheet: ExcelJS.Worksheet,
    _styles: ExcelStyles,
    config: ExcelSectionConfig,
    currentRow: number,
    addSectionTitle: AddSectionTitleFn,
    addTableHeaders: AddTableHeadersFn,
    addDataRow: AddDataRowFn,
  ): number => {
    const { data, showColourwayColumn, currencySymbol } = config;

    if (!data?.productTemplate?.isAdditionalComponentActive) {
      return currentRow;
    }

    const buildAdditionalHeaders = (): string[] => {
      const headers: string[] = [];
      if (showColourwayColumn) headers.push('Colourway');
      headers.push('Item', 'Quantity', 'UoM', 'Price Per Unit', 'Total Price', 'Exclude');
      return headers;
    };

    const buildAdditionalDataRow = (item: ExportAdditionalRow): ExportCellValue[] => {
      const row: ExportCellValue[] = [];
      if (showColourwayColumn) {
        row.push(item?.colourway?.name || '');
      }

      const quantity = toFloat(item.quantityPerColSizLen);
      const pricePerUnit = toFloat(item.pricePerUoM);
      const totalPrice = toFloat(item.totalPrice);
      const isExcluded = item?.isExcluded || false;
      const excludeSymbol = isExcluded ? '✓' : '✗';

      row.push(
        item?.title || '',
        quantity.toFixed(2),
        item?.quantityUom?.name || '',
        `${currencySymbol}${pricePerUnit.toFixed(2)}`,
        `${currencySymbol}${totalPrice.toFixed(2)}`,
        excludeSymbol,
      );
      return row;
    };

    const getAdditionalNumericIndices = (): number[] => {
      const indices: number[] = [];
      let colIndex = 0;
      if (showColourwayColumn) colIndex++;
      colIndex++;
      indices.push(colIndex);
      colIndex += 2;
      indices.push(colIndex);
      indices.push(colIndex + 1);
      return indices;
    };

    const buildAdditionalTotalRow = (total: number): ExportCellValue[] => {
      const row: ExportCellValue[] = [];
      if (showColourwayColumn) row.push('');
      row.push('', '', '', '', `${currencySymbol}${total.toFixed(2)}`, '');
      return row;
    };

    const additionalHeaders = buildAdditionalHeaders();
    const additionalNumericIndices = getAdditionalNumericIndices();
    const maxAdditionalCols = additionalHeaders.length;

    currentRow = addSectionTitle(data?.productTemplate?.additionalComponentTitle || 'Additional Components', maxAdditionalCols, currentRow);
    currentRow = addTableHeaders(additionalHeaders, currentRow);

    let additionalTotal = 0;

    if ((data.additionalComponent?.additionalComponentInfo?.length ?? 0) > 0) {
      data?.additionalComponent?.additionalComponentInfo?.forEach((item) => {
        if (item.title || item.quantityPerColSizLen || item.pricePerUoM || item.totalPrice) {
          const dataRow = buildAdditionalDataRow(item);
          const totalPrice = toFloat(item.totalPrice);
          if (!item?.isExcluded) {
            additionalTotal += totalPrice;
          }
          currentRow = addDataRow(dataRow, currentRow, false, additionalNumericIndices);
        }
      });

      const totalRow = buildAdditionalTotalRow(additionalTotal);
      currentRow = addDataRow(totalRow, currentRow, true, [additionalNumericIndices[additionalNumericIndices.length - 1] as number]);
    }

    return currentRow + 1;
  };

  const addSetUpCostSection = (
    _worksheet: ExcelJS.Worksheet,
    _styles: ExcelStyles,
    config: ExcelSectionConfig,
    currentRow: number,
    addSectionTitle: AddSectionTitleFn,
    addTableHeaders: AddTableHeadersFn,
    addDataRow: AddDataRowFn,
  ): number => {
    const { data, showColourwayColumn, currencySymbol } = config;

    if (!data?.productTemplate?.isSetUpCostActive) {
      return currentRow;
    }

    const buildAdditionalHeaders = (): string[] => {
      const headers: string[] = [];
      if (showColourwayColumn) headers.push('Colourway');
      headers.push('Item', 'Quantity', 'UoM', 'Price Per Unit', 'Total Price', 'Exclude');
      return headers;
    };

    const buildAdditionalDataRow = (item: ExportAdditionalRow): ExportCellValue[] => {
      const row: ExportCellValue[] = [];
      if (showColourwayColumn) {
        row.push(item?.colourway?.name || '');
      }

      const quantity = toFloat(item.quantityPerColSizLen);
      const pricePerUnit = toFloat(item.pricePerUoM);
      const totalPrice = toFloat(item.totalPrice);
      const isExcluded = item?.isExcluded || false;
      const excludeSymbol = isExcluded ? '✓' : '✗';

      row.push(
        item?.title || '',
        quantity.toFixed(2),
        item?.quantityUom?.name || '',
        `${currencySymbol}${pricePerUnit.toFixed(2)}`,
        `${currencySymbol}${totalPrice.toFixed(2)}`,
        excludeSymbol,
      );
      return row;
    };

    const getAdditionalNumericIndices = (): number[] => {
      const indices: number[] = [];
      let colIndex = 0;
      if (showColourwayColumn) colIndex++;
      colIndex++;
      indices.push(colIndex);
      colIndex += 2;
      indices.push(colIndex);
      indices.push(colIndex + 1);
      return indices;
    };

    const buildAdditionalTotalRow = (total: number): ExportCellValue[] => {
      const row: ExportCellValue[] = [];
      if (showColourwayColumn) row.push('');
      row.push('', '', '', '', `${currencySymbol}${total.toFixed(2)}`, '');
      return row;
    };

    const additionalHeaders = buildAdditionalHeaders();
    const additionalNumericIndices = getAdditionalNumericIndices();
    const maxSetupCols = additionalHeaders.length;

    currentRow = addSectionTitle('Set Up Cost', maxSetupCols, currentRow);
    currentRow = addTableHeaders(additionalHeaders, currentRow);

    let setupTotal = 0;

    if ((data.additional?.additionalInfo?.length ?? 0) > 0) {
      data?.additional?.additionalInfo?.forEach((item) => {
        if (item.title || item.quantityPerColSizLen || item.pricePerUoM || item.totalPrice) {
          const dataRow = buildAdditionalDataRow(item);
          const totalPrice = toFloat(item.totalPrice);
          if (!item?.isExcluded) {
            setupTotal += totalPrice;
          }
          currentRow = addDataRow(dataRow, currentRow, false, additionalNumericIndices);
        }
      });

      const totalRow = buildAdditionalTotalRow(setupTotal);
      currentRow = addDataRow(totalRow, currentRow, true, [additionalNumericIndices[additionalNumericIndices.length - 1] as number]);
    }

    return currentRow + 1;
  };

  /**
   * Writes the Spec Requirements section (BM-9899).
   *
   * Two plain text columns and no totals row — unlike every other section here, nothing in it is
   * numeric, so there is no numeric-index list and no currency formatting. Gated on the costing's
   * embedded template snapshot, the same flag the on-screen card uses, so the spreadsheet and the
   * page agree about whether the section exists.
   */
  const addSpecificationRequirementSection = (
    _worksheet: ExcelJS.Worksheet,
    _styles: ExcelStyles,
    config: ExcelSectionConfig,
    currentRow: number,
    addSectionTitle: AddSectionTitleFn,
    addTableHeaders: AddTableHeadersFn,
    addDataRow: AddDataRowFn,
  ): number => {
    const { data } = config;

    if (!data?.productTemplate?.isSpecificationRequirementActive) {
      return currentRow;
    }

    const specificationHeaders = ['Requirement', 'Specification'];

    currentRow = addSectionTitle(
      (data?.productTemplate?.specificationRequirementTitle as string | undefined) || 'Spec Requirements',
      specificationHeaders.length,
      currentRow,
    );
    currentRow = addTableHeaders(specificationHeaders, currentRow);

    data?.specificationRequirement?.specificationRequirementInfo?.forEach((item) => {
      currentRow = addDataRow([item?.requirement || '', item?.specification || ''], currentRow, false, []);
    });

    return currentRow + 1;
  };

  const addTestingSection = (
    _worksheet: ExcelJS.Worksheet,
    _styles: ExcelStyles,
    config: ExcelSectionConfig,
    currentRow: number,
    addSectionTitle: AddSectionTitleFn,
    addTableHeaders: AddTableHeadersFn,
    addDataRow: AddDataRowFn,
  ): number => {
    const { data, currencySymbol, showColourwayColumn } = config;

    if (!data?.testing?.testingInfo?.length) {
      return currentRow;
    }

    const buildTestingHeaders = (): string[] => {
      const headers: string[] = [];
      if (showColourwayColumn) {
        headers.push('Colourway');
      }
      headers.push('Testing Code', 'Testing Description', 'Testing Type', 'BOM Component', 'Colour Ref', 'Test Price', 'Price Per Unit', 'Exclude');
      return headers;
    };

    const buildTestingDataRow = (testItem: ExportTestingRow): ExportCellValue[] => {
      const row: ExportCellValue[] = [];
      if (showColourwayColumn) {
        const colourwayName = testItem?.colourway?.name || testItem?.colourway?.newReferences || '';
        row.push(colourwayName);
      }

      const testingCode = testItem?.testingCode?.code || '';
      const description = testItem?.testingCode?.description || '';
      const testType = testItem?.testingCode?.type?.name || '';
      const bomComponent = testItem?.testingCode?.bomComponent?.referenceCode || '--';
      const colourRef =
        testItem?.colourRef?.name || testItem?.colourRef?.newReferences || testItem?.colourway?.colourRef || testItem?.colourway?.newReferences || '--';
      const testPrice = toFloat(testItem?.totalPrice);
      const pricePerUnit = toFloat(testItem?.pricePerUnit);
      const isExcluded = testItem?.isExcluded || false;
      const excludeSymbol = isExcluded ? '✓' : '✗';

      row.push(
        testingCode,
        description,
        testType,
        bomComponent,
        colourRef,
        `${currencySymbol}${testPrice.toFixed(2)}`,
        `${currencySymbol}${pricePerUnit.toFixed(2)}`,
        excludeSymbol,
      );

      return row;
    };

    const getTestingNumericIndices = (): number[] => {
      const indices: number[] = [];
      let colIndex = 0;

      if (showColourwayColumn) colIndex++;
      colIndex += 2;
      colIndex++;
      colIndex++;
      colIndex++;
      indices.push(colIndex);
      indices.push(colIndex + 1);

      return indices;
    };

    const buildTestingTotalRow = (total: number): ExportCellValue[] => {
      const row: ExportCellValue[] = [];
      if (showColourwayColumn) {
        row.push('');
      }
      row.push('', '', '', '', '', `${currencySymbol}${total.toFixed(2)}`, '', '');
      return row;
    };

    const testingHeaders = buildTestingHeaders();
    const testingNumericIndices = getTestingNumericIndices();
    const maxTestingCols = testingHeaders.length;

    currentRow = addSectionTitle('Testing', maxTestingCols, currentRow);
    currentRow = addTableHeaders(testingHeaders, currentRow);

    let testingTotal = 0;

    data?.testing?.testingInfo?.forEach((testItem) => {
      const dataRow = buildTestingDataRow(testItem);

      if (!testItem?.isExcluded) {
        const testPrice = toFloat(testItem?.totalPrice);
        testingTotal += testPrice;
      }

      currentRow = addDataRow(dataRow, currentRow, false, testingNumericIndices);
    });

    const totalRow = buildTestingTotalRow(testingTotal);
    currentRow = addDataRow(totalRow, currentRow, true, [testingNumericIndices[0] as number]);

    return currentRow + 1;
  };

  const addGrandTotalSection = (
    _worksheet: ExcelJS.Worksheet,
    _styles: ExcelStyles,
    config: ExcelSectionConfig,
    currentRow: number,
    addSectionTitle: AddSectionTitleFn,
    addTableHeaders: AddTableHeadersFn,
    addDataRow: AddDataRowFn,
  ): number => {
    const { data, showColourwayColumn, showSizeColumn, showLengthColumn, currencySymbol } = config;

    const buildGrandTotalHeaders = (): string[] => {
      const headers: string[] = [];
      if (showColourwayColumn) {
        headers.push('Colourway');
      }
      if (showSizeColumn) {
        headers.push('Size');
      }
      if (showLengthColumn) {
        headers.push('Length');
      }
      headers.push('Quantity', 'Price Per Unit', 'Total Cost Price');
      return headers;
    };

    const buildGrandTotalDataRowFromExpandData = (expandItem: ExportExpandRow, grandItem: ExportParentRow): ExportCellValue[] => {
      const row: ExportCellValue[] = [];

      if (showColourwayColumn) {
        const colourwayName = grandItem.colourway?.name || grandItem.colourway?.newReferences || '';
        row.push(colourwayName);
      }
      if (showSizeColumn) {
        row.push(expandItem?.size?.toUpperCase() || '');
      }
      if (showLengthColumn) {
        const lengthName =
          data.averageCosting?.averageCostingInfo
            ?.find((item) => item.colourway?._id === grandItem.colourway?._id)
            ?.expandData?.find((exp) => exp?.size === expandItem?.size)?.length?.name || '';
        row.push(lengthName);
      }

      const quantity = toFloat(expandItem?.quantityPerSizCol);
      const pricePerUnit = toFloat(expandItem?.pricePerUnit);
      const totalPrice = toFloat(expandItem?.totalPrice);

      row.push(quantity.toFixed(2), `${currencySymbol}${pricePerUnit.toFixed(2)}`, `${currencySymbol}${totalPrice.toFixed(2)}`);

      return row;
    };

    const getGrandTotalNumericIndices = (): number[] => {
      const indices: number[] = [];
      let colIndex = 0;

      if (showColourwayColumn) colIndex++;
      if (showSizeColumn) colIndex++;
      if (showLengthColumn) colIndex++;

      indices.push(colIndex);
      colIndex++;
      indices.push(colIndex);
      colIndex++;
      indices.push(colIndex);

      return indices;
    };

    const grandTotalHeaders = buildGrandTotalHeaders();
    const grandTotalNumericIndices = getGrandTotalNumericIndices();
    const maxGrandTotalCols = grandTotalHeaders.length;

    currentRow = addSectionTitle('Grand Total', maxGrandTotalCols, currentRow);
    currentRow = addTableHeaders(grandTotalHeaders, currentRow);

    if ((data.grandTotal?.grandTotalInfo?.length ?? 0) > 0) {
      data?.grandTotal?.grandTotalInfo?.forEach((grandItem) => {
        if (grandItem?.expandData && Array.isArray(grandItem.expandData)) {
          grandItem.expandData.forEach((expandItem) => {
            const dataRow = buildGrandTotalDataRowFromExpandData(expandItem, grandItem);
            currentRow = addDataRow(dataRow, currentRow, false, grandTotalNumericIndices);
          });
        }
      });
    }

    return currentRow + 1;
  };

  const addLandedInfoSection = (
    worksheet: ExcelJS.Worksheet,
    styles: ExcelStyles,
    config: ExcelSectionConfig,
    currentRow: number,
    _addSectionTitle: AddSectionTitleFn,
    addTableHeaders: AddTableHeadersFn,
    addDataRow: AddDataRowFn,
  ): number => {
    const { data, showColourwayColumn, showSizeColumn, showLengthColumn } = config;

    const dcEntries = (data?.landedInformations || []).filter((entry) => entry?.landedInfoArr?.length > 0);
    if (!dcEntries.length) {
      return currentRow;
    }

    const templateDetail: ExportTemplateDetail = costingTemplateDetail || (internalState?.currSelectedTemplateDetails as unknown as ExportTemplateDetail) || {};
    const topColumnArr = (templateDetail?.topColumnArr || []).filter((col) =>
      ['Price Per Unit (Converted)', 'Total (Converted)'].includes(col?.title as string),
    );
    const defaultColumns = templateDetail?.defaultColumns || [];
    const bottomColumnArr = templateDetail?.bottomColumnArr || [];

    let landedInfoColumns: ExportTemplateColumn[] = [];
    if (landedInformationColumns?.length && productCostingDetail) {
      landedInfoColumns = landedInformationColumns as ExportTemplateColumn[];
    } else if (!productCostingDetail) {
      landedInfoColumns = (internalState?.currSelectedTemplateDetails?.landedInfo as ExportTemplateColumn[] | undefined)?.filter((col) => col?.isActive) || [];
    }

    const formatDateTime = (dateString?: string): string => {
      if (!dateString) return '';
      const date = new Date(dateString);
      const day = date.getDate();
      const month = date.toLocaleString('en-US', { month: 'short' });
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${day}-${month}-${year} at ${hours}:${minutes}`;
    };

    const formatPercentWithCalc = (cur: string, baseValue: unknown, calculatedValue: unknown): string => {
      if (calculatedValue && calculatedValue !== '' && calculatedValue !== '0') {
        return `${cur}${toFloat(calculatedValue).toFixed(2)} (${toFloat(baseValue).toFixed(2)}%)`;
      }
      return `${toFloat(baseValue).toFixed(2)}%`;
    };

    const buildLandedHeaders = (showDcColumn: boolean): string[] => {
      const headers: string[] = [];

      if (showDcColumn) {
        headers.push('Distribution Centre');
      }
      if (showColourwayColumn) {
        headers.push('Colourway');
      }
      if (showSizeColumn) {
        headers.push('Size');
      }
      if (showLengthColumn) {
        headers.push('Length');
      }

      topColumnArr.forEach((col) => {
        headers.push((col?.aliasDisplayName || col?.title) as string);
      });

      landedInfoColumns.forEach((col) => {
        if (!col?.isActive) return;
        headers.push(`${col?.aliasDisplayName || col?.title}${col?.type?.includes('percentage') ? ' (%)' : ''}`);
      });

      defaultColumns.forEach((col) => {
        headers.push((col?.aliasDisplayName || col?.title) as string);
      });

      bottomColumnArr.forEach((col) => {
        headers.push((col?.aliasDisplayName || col?.title) as string);
      });

      return headers;
    };

    const buildLandedDataRow = (landedItem: ExportLandedRow, entry: ExportLandedInformation, cur: string, showDcColumn: boolean): ExportCellValue[] => {
      const row: ExportCellValue[] = [];

      if (showDcColumn) {
        row.push(entry.distributionCentreName);
      }
      if (showColourwayColumn) {
        row.push(landedItem?.colourWay?.name || landedItem?.colourway?.name || landedItem?.colourway?.newReferences || '');
      }
      if (showSizeColumn) {
        const sizeValue = isBreakdown && Array.isArray(landedItem?.sizeRange) ? landedItem.sizeRange.join(' - ') : landedItem?.size || '';
        row.push(String(sizeValue).toUpperCase());
      }
      if (showLengthColumn) {
        const lengthValue =
          isBreakdown && Array.isArray(landedItem?.lengthRange) ? landedItem.lengthRange.map((item) => item?.name).join(' - ') : landedItem?.length?.name || '';
        row.push(lengthValue);
      }

      topColumnArr.forEach((col) => {
        row.push(`${cur}${toFloat(landedItem?.[col?.aliasName as string]).toFixed(2)}`);
      });

      landedInfoColumns.forEach((col, k) => {
        if (!col?.isActive) return;
        const cellInfo = landedItem?.landedInfo?.[k] || {};
        if (col?.type === 'percentage') {
          row.push(formatPercentWithCalc(cur, cellInfo.tempValue, cellInfo.calculatedValue));
        } else {
          row.push(`${cur}${toFloat(cellInfo.tempValue).toFixed(2)}`);
        }
      });

      defaultColumns.forEach((col) => {
        const aliasName = col?.aliasName as string;
        const baseValue = landedItem?.[aliasName];
        const calculatedValue = landedItem?.[`${aliasName}CalculatedValue`];

        if (col?.type === 'percentage' && col?.isDefault && !['licenseRoyaltyFee', 'likenessRoyaltyFee'].includes(aliasName)) {
          row.push(formatPercentWithCalc(cur, baseValue, calculatedValue));
        } else if (aliasName === 'agentsFee') {
          row.push(`${cur}${toFloat(calculatedValue).toFixed(2)}`);
        } else if (['licenseRoyaltyFee', 'likenessRoyaltyFee'].includes(aliasName)) {
          row.push(`${cur}${toFloat(calculatedValue).toFixed(2)} (${toFloat(baseValue).toFixed(2)}%)`);
        } else if (['freightValue', 'freightAutomated'].includes(aliasName) && col?.type === 'amount') {
          row.push(`${cur}${toFloat(baseValue).toFixed(2)}`);
        } else {
          row.push((baseValue as ExportCellValue) || '0');
        }
      });

      bottomColumnArr.forEach((col) => {
        const aliasName = col?.aliasName as string;
        const baseValue = landedItem?.[aliasName];
        const calculatedValue = landedItem?.[`${aliasName}CalculatedValue`];

        if (baseValue === '--') {
          row.push('--');
        } else if (['sellingPriceGross', 'estimatedRetailValue'].includes(aliasName) && col?.isEditable) {
          row.push(`${cur}${toFloat(baseValue).toFixed(2)}`);
        } else if (col?.isEditable && ['vat', 'customerDiscount'].includes(aliasName)) {
          row.push(formatPercentWithCalc(cur, baseValue, calculatedValue));
        } else if (['marginPerUnit', 'retailMarginPerUnit'].includes(aliasName)) {
          row.push(toFloat(baseValue).toFixed(2));
        } else {
          row.push(`${cur}${toFloat(baseValue).toFixed(2)}`);
        }
      });

      return row;
    };

    dcEntries.forEach((entry) => {
      const exchangeInfo = entry?.landedExchangeRateInformation || {};
      const showDcColumn = !!entry?.distributionCentreName;
      const dcCurrency = entry?.distributionCentreCurrency || userInfo?.companyCurrency;
      const cur = checkCurrency(dcCurrency || exchangeInfo?.companyCurrency, null, true);
      const landedHeaders = buildLandedHeaders(showDcColumn);
      const maxLandedCols = landedHeaders.length;

      const defaultRateValue =
        productExchnageRate?.defaultCurrencyValue && productExchnageRate?.defaultCurrencyValue !== '' ? productExchnageRate.defaultCurrencyValue : 1;
      const leftValue = exchangeInfo?.companyCurrency ? exchangeInfo?.companyCurrencyValue : defaultRateValue;
      const leftCurrency = exchangeInfo?.companyCurrency || dcCurrency || '';
      const exchangedRate = exchangeInfo?.manualAdded ? exchangeInfo?.exchangedRate ?? 1 : internalState?.primaryCurrValue ?? 1;
      const rightCurrency = exchangeInfo?.supplierCurrency || data?.productInfo?.currency || '';
      const manualText =
        exchangeInfo?.manualAdded && exchangeInfo?.updatedBy
          ? ` (Manual) - Amended by ${exchangeInfo.updatedBy} on ${formatDateTime(exchangeInfo.updatedAt)}`
          : '';
      const exchangeText = `Exchange Rate: ${leftValue} ${leftCurrency} = ${exchangedRate} ${rightCurrency}${manualText}`;

      const landedTitleCell = worksheet.getCell(currentRow, 1);
      landedTitleCell.value = 'Landed Info';
      landedTitleCell.style = {
        ...styles.sectionTitle,
        alignment: { horizontal: 'left', vertical: 'middle' },
      };

      const exchangeStartCol = 2;
      const exchangeCell = worksheet.getCell(currentRow, exchangeStartCol);
      exchangeCell.value = exchangeText;
      exchangeCell.style = {
        font: { name: 'Aktiv Grotesk', size: 10 },
        alignment: { horizontal: 'left', vertical: 'middle' },
      } as Partial<ExcelJS.Style> as ExcelJS.Style;

      if (maxLandedCols > exchangeStartCol) {
        worksheet.mergeCells(currentRow, exchangeStartCol, currentRow, maxLandedCols);
      }

      currentRow++;

      currentRow = addTableHeaders(landedHeaders, currentRow);

      entry.landedInfoArr.forEach((landedItem) => {
        const dataRow = buildLandedDataRow(landedItem, entry, cur, showDcColumn);
        currentRow = addDataRow(dataRow, currentRow, false, []);
      });

      currentRow += 1;
    });

    return currentRow;
  };

  const saveExcelFile = async (workbook: ExcelJS.Workbook, data: CostingExportData): Promise<void> => {
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `Costing_${data.productInfo?.reference || 'Export'}_${timestamp}.xlsx`;

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer as BlobPart], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return {
    handleCostingDownload,
    generateCostingExcel,
  };
};

export default useExcelDownload;
